import type { Key } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Select } from '@backstage/ui';
import { FiltersIcon } from '../../components/icons';
import type {
  CreatedAfterKey,
  NotificationsFilters,
  NotificationsView,
  SeverityKey,
  SortByKey,
} from './types';

/*
 * Card geometry mirrors `DocListPanel`: 12px radius, hairline ring, a titled
 * header rule over the body. The selects themselves are BUI `Select`s and take
 * their treatment from the shared theme, so nothing here colours them.
 */
const useStyles = makeStyles({
  panel: {
    borderRadius: 12,
    background: 'var(--bui-white)',
    boxShadow: 'var(--hone-portal-elevation-card)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 20px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  icon: {
    width: 14,
    height: 14,
    opacity: 0.5,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '18px 20px 20px',
  },
});

/** Sentinel for "any topic" — BUI `Select` has no empty-value option. */
const ANY_TOPIC = '___all___';

const VIEW_OPTIONS = [
  { value: 'unread', label: 'Unread notifications' },
  { value: 'read', label: 'Read notifications' },
  { value: 'saved', label: 'Saved' },
  { value: 'all', label: 'All' },
];

const CREATED_AFTER_OPTIONS = [
  { value: 'last24h', label: 'Last 24h' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'all', label: 'Any time' },
];

const SORT_BY_OPTIONS = [
  { value: 'newest', label: 'Newest on top' },
  { value: 'oldest', label: 'Oldest on top' },
  { value: 'topic', label: 'Topic' },
  { value: 'origin', label: 'Origin' },
];

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

/** BUI single-selects hand back `Key | Key[] | null`; narrow to one string. */
function single(key: Key | Key[] | null): string | undefined {
  const value = Array.isArray(key) ? key[0] : key;
  return typeof value === 'string' ? value : undefined;
}

export type NotificationsFilterPanelProps = {
  filters: NotificationsFilters;
  onChange: (filters: NotificationsFilters) => void;
  allTopics: string[];
};

export function NotificationsFilterPanel({
  filters,
  onChange,
  allTopics,
}: NotificationsFilterPanelProps) {
  const classes = useStyles();
  const topicOptions = [
    { value: ANY_TOPIC, label: 'Any topic' },
    ...[...allTopics]
      .sort((a, b) => a.localeCompare(b))
      .map(topic => ({ value: topic, label: topic })),
  ];

  return (
    <section className={classes.panel}>
      <header className={classes.header}>
        <FiltersIcon className={classes.icon} />
        Filters
      </header>
      <div className={classes.fields}>
        <Select
          label="View"
          value={filters.view}
          options={VIEW_OPTIONS}
          onChange={key => {
            const view = single(key) as NotificationsView | undefined;
            if (view) onChange({ ...filters, view });
          }}
        />
        <Select
          label="Sent out"
          value={filters.createdAfter}
          options={CREATED_AFTER_OPTIONS}
          onChange={key => {
            const createdAfter = single(key) as CreatedAfterKey | undefined;
            if (createdAfter) onChange({ ...filters, createdAfter });
          }}
        />
        <Select
          label="Sort by"
          value={filters.sortBy}
          options={SORT_BY_OPTIONS}
          onChange={key => {
            const sortBy = single(key) as SortByKey | undefined;
            if (sortBy) onChange({ ...filters, sortBy });
          }}
        />
        <Select
          label="Min severity"
          value={filters.severity}
          options={SEVERITY_OPTIONS}
          onChange={key => {
            const severity = single(key) as SeverityKey | undefined;
            if (severity) onChange({ ...filters, severity });
          }}
        />
        <Select
          label="Topic"
          value={filters.topic ?? ANY_TOPIC}
          options={topicOptions}
          onChange={key => {
            const topic = single(key);
            onChange({
              ...filters,
              topic: !topic || topic === ANY_TOPIC ? undefined : topic,
            });
          }}
        />
      </div>
    </section>
  );
}
